import React, { useState } from 'react';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import * as yup from 'yup';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import InputField from '../../../../Components/Form/components/InputField';
import useTranslation from '../../../../Hooks/useTranslation';
import ECardFolderSelectField from '../../../Base/Components/Fields/ECardFolder/ECardFolderSelectField';
import FormMultiplier from '../../../../Components/Form/FormMultiplier';
import { FileToShare } from '../ECard/ECard';
import FormContainer from '../../../../Components/Form/FormContainer';
// @ts-ignore
import styles from './ECardMoveFileToHealthDocumentation.module.scss';
import {
	useTransferDocumentMutation,
	useTransferDocumentsMutation,
} from '../../../../graphql/graphql';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import usePatientConsent from '../../../../Components/PatientProfile/Hooks/usePatientConsent';
import PatientNoConsentProvided from '../../../Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import Loading from '../../../../Components/Loading';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';

interface Props {
	patientId: string;
	targetPatientId: string;
	onSuccess: () => void;
	filesToShare: FileToShare[];
	isPatientsConsentRequired: boolean;
}

interface FileToTransfer {
	id: string;
	fileName: string;
	category: SelectValue;
	groupFiles: { id: string; add: boolean }[];
}

const ECardMoveFileToHealthDocumentation: React.FC<Props> = ({
	onSuccess,
	targetPatientId,
	filesToShare,
	patientId,
	isPatientsConsentRequired,
}) => {
	const { t } = useTranslation();
	const [transferDocuments] = useTransferDocumentsMutation();
	const [transferDocument] = useTransferDocumentMutation();
	const { isConsentProvided } = usePatientConsent(patientId);
	const [isLoading, setIsLoading] = useState(false);

	const validationSchema = yup.object({
		files: yup
			.array()
			.of(
				yup.object().shape({
					fileName: yup
						.string()
						.required(
							t('validation.eCardMoveFileToHealthDocumentation.fileName'),
						),
					category: yup.object().shape({
						value: yup
							.string()
							.uuid()
							.required(
								t('validation.eCardMoveFileToHealthDocumentation.category'),
							),
					}),
				}),
			)
			.required(),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: {
			files: filesToShare.map(item => {
				return {
					id: item.id,
					fileName: item.name,
					fileId: item.id,
					groupFiles: item.file?.groupFiles?.map(groupFile => {
						return {
							id: groupFile?.id,
							add: true,
						};
					}),
				};
			}),
		},
	});

	const handleSaveMultiple = async (values: any) => {
		setIsLoading(true);
		if (values?.files?.length) {
			const files = values.files as FileToTransfer[];
			await Promise.all(
				files.map(async (item: FileToTransfer) => {
					const documentIds: string[] =
						item.groupFiles.length > 0
							? item.groupFiles.filter(i => i.add).map(i => i.id)
							: [item.id];
					await transferDocuments({
						input: {
							targetFolder: item.category.value,
							documentIds,
							grouped: documentIds.length > 1,
							documentName: item.fileName,
							targetUserId: targetPatientId,
						},
					});
				}),
			);
		}
		onSuccess();
		setIsLoading(false);
	};

	const handleSave = async (values: any) => {
		setIsLoading(true);
		if (values?.files?.length) {
			const files = values.files as FileToTransfer[];
			await Promise.all(
				files.map(async (item: FileToTransfer) => {
					await transferDocument({
						input: {
							targetFolder: item.category.value,
							documentId: item.id,
							documentName: item.fileName,
							targetUserId: targetPatientId,
						},
					});
				}),
			);
		}
		onSuccess();
		setIsLoading(false);
	};

	if (!isConsentProvided && isPatientsConsentRequired) {
		return <PatientNoConsentProvided patientId={patientId} />;
	}

	if (isLoading) {
		return <Loading />;
	}

	return (
		<FormWrapper formMethods={formMethods} registerForChanges>
			<FormMultiplier name='files' allowAddForm={false}>
				{({ index }) => (
					<>
						<FormContainer settings={{ showLine: true }}>
							<div className='row'>
								<div className='col d-none'>
									<InputField
										name='id'
										label='fileId'
										className='mb-3'
										isRequired
									/>
								</div>
								<div className='col'>
									<InputField
										name='fileName'
										label='component.eCard.moveFileAs'
										className='mb-3'
										isRequired
									/>
								</div>
								<div className='col'>
									<ECardFolderSelectField
										patientId={targetPatientId}
										name='category'
										label='component.eCard.moveFileToFolder'
										className='mb-3'
										isRequired
									/>
								</div>
							</div>
							<div className='row d-none'>
								<FormMultiplier
									name='groupFiles'
									allowAddForm={false}
									className={styles.groupFiles}>
									{item => {
										return (
											<div className={styles.file}>
												<img
													src={
														filesToShare[index]?.file?.groupFiles[item.index]
															?.thumbUrl
													}
												/>
												<div className='d-none'>
													<InputField name='id' isRequired />
												</div>
												<CheckboxField name='add' className={styles.checkbox} />
											</div>
										);
									}}
								</FormMultiplier>
							</div>
						</FormContainer>
						<InputField name='fileId' className='d-none mb-3' isRequired />
					</>
				)}
			</FormMultiplier>
			<div className={'mt-4'}>
				<SubmitButton
					title={'component.eCard.transfer'}
					onSubmit={handleSave}
				/>
			</div>
		</FormWrapper>
	);
};

export default ECardMoveFileToHealthDocumentation;
